import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "@phosphor-icons/react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { clsx } from "clsx";
import { kfModalContentIn, kfOverlayIn } from "@/lib/emotionTw";
import { IconButton } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import type { ModalProps, ModalSize } from "./Modal.types";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. The backdrop-blur and translate chains are
 * inlined to their computed equivalents so the rules stay self-contained.
 * Verified against the frozen baseline. */

const modalSizeStyles: Record<ModalSize, CSSObject> = {
  sm: { maxWidth: "24rem" },
  md: { maxWidth: "32rem" },
  lg: { maxWidth: "42rem" },
  xl: { maxWidth: "56rem" },
  full: { maxWidth: "calc(100vw - 2rem)" }
};

const ModalOverlay = styled(RadixDialog.Overlay, {
  label: "AgenticModal-overlay"
})({
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 50,
  backgroundColor: "hsl(var(--bfrs-color-text-primary) / .4)",
  WebkitBackdropFilter: "blur(1px)",
  backdropFilter: "blur(1px)",
  ...kfOverlayIn,
  animation: "bfrs-bfrs-overlay-in var(--bfrs-motion-fast) var(--bfrs-ease-out)"
});

const ModalContent = styled(RadixDialog.Content, {
  label: "AgenticModal-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: ModalSize } }>(({ ownerState }) => [
  {
    position: "fixed",
    left: "50%",
    top: "50%",
    zIndex: 50,
    display: "flex",
    maxHeight: "calc(100dvh - 2rem)",
    width: "calc(100% - 2rem)",
    "--tw-translate-x": "-50%",
    "--tw-translate-y": "-50%",
    "--tw-rotate": "0",
    "--tw-skew-x": "0",
    "--tw-skew-y": "0",
    "--tw-scale-x": "1",
    "--tw-scale-y": "1",
    transform:
      "translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-xl)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-overlay)",
    "--tw-shadow-colored": "var(--bfrs-shadow-overlay)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    ...kfModalContentIn,
    animation: "bfrs-bfrs-modal-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  modalSizeStyles[ownerState.size]
]);

const ModalHeader = styled("div", {
  label: "AgenticModal-header"
})({
  display: "flex",
  alignItems: "flex-start",
  gap: "1rem",
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

const ModalHeaderText = styled("div", {
  label: "AgenticModal-headerText"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const ModalTitle = styled(RadixDialog.Title, {
  label: "AgenticModal-title"
})({
  fontSize: "1rem",
  lineHeight: "1.5rem",
  fontWeight: 700,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const ModalDescription = styled(RadixDialog.Description, {
  label: "AgenticModal-description"
})({
  marginTop: ".25rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const ModalBody = styled("div", {
  label: "AgenticModal-body"
})({
  minHeight: "0px",
  overflow: "auto",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

const ModalFooter = styled("div", {
  label: "AgenticModal-footer"
})({
  display: "flex",
  flexDirection: "column-reverse",
  gap: ".5rem",
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / .6)",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  "@media (min-width:640px)": {
    flexDirection: "row",
    justifyContent: "flex-end"
  }
});

export function Modal({
  open,
  defaultOpen,
  onOpenChange,
  onClose,
  trigger,
  title,
  description,
  children,
  footer,
  size = "md",
  closeButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  preventClose = false,
  className,
  contentClassName,
  headerClassName,
  footerClassName
}: ModalProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && preventClose) return;
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  };

  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <BfrsPortalScope>
          <ModalOverlay />
          <ModalContent
            ownerState={{ size }}
            className={clsx(className, contentClassName)}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick || preventClose) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape || preventClose) event.preventDefault();
            }}
          >
            {(title || description || closeButton) && (
              <ModalHeader className={headerClassName}>
                <ModalHeaderText>
                  {title && <ModalTitle>{title}</ModalTitle>}
                  {description && <ModalDescription>{description}</ModalDescription>}
                </ModalHeaderText>
                {closeButton && (
                  <RadixDialog.Close asChild>
                    <IconButton aria-label="Close modal" icon={<X size={16} />} variant="outline" size="sm" disabled={preventClose} />
                  </RadixDialog.Close>
                )}
              </ModalHeader>
            )}
            <ModalBody>{children}</ModalBody>
            {footer && <ModalFooter className={footerClassName}>{footer}</ModalFooter>}
          </ModalContent>
        </BfrsPortalScope>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
